import { useState } from 'react';
import { Controller } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { CSS, styled } from '@/stitches.config';
import { IMy24LineupItem, IMy24Theme, IMy24Tracking } from '@/types/shared';

const LinupMenuText = styled(Text, {
  width: '100%',
  height: 48,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  cursor: 'pointer',
  us: 'none',
  '@lg': {
    height: 60,
  },
});

const StyledContent = styled('div', {
  '& .swiper': {
    overflow: 'initial',
    width: `clamp(192px, ${-360 * ((264 - 192) / 920) + 192}px + ${
      ((264 - 192) / 920) * 100
    }vw, 264px)`,
  },
  variants: {
    shadow: {
      true: {
        [`& ${LinupMenuText}`]: {
          textShadow: 'rgba(0, 0, 0, 0.16) 0px 2px 4px',
        },
      },
    },
  },
});

interface LineupMenuSliderProps
  extends React.ComponentProps<typeof StyledContent> {
  css?: CSS;
  className?: string;
  theme?: IMy24Theme;
  items?: IMy24LineupItem[];
  dataTest?: string;
  dataTrack?: IMy24Tracking;
  onClickText?: (index: number) => void;
  slideIndex?: number;
  onSwiper?: (swiper: any) => void;
  control?: any;
  initialSlide?: number;
  speed?: number;
  loop?: boolean;
  centeredSlidesBounds?: boolean;
}

export const LineupMenuSlider = ({
  theme = 'light',
  initialSlide = 0,
  speed = 2000,
  loop,
  control,
  ...props
}: LineupMenuSliderProps) => {
  const _onClick = (index: number) => {
    if (index === slideActive) return;
    props?.onClickText && props?.onClickText(index);
  };

  const [slideActive, setSlideActive] = useState<number>(0);

  const onItemSlideChange = (index: number) => {
    setSlideActive(index);
  };

  const options = {
    shadow: props?.shadow,
  };

  const textVariant = (id: number, _theme: IMy24Theme) => {
    switch (theme) {
      case 'dark':
        if (id === props?.slideIndex || id === slideActive) {
          if (_theme === 'dark') return 'text-white';
          return 'text-gray-darkest';
        }
        return 'text-white';
      default:
        if (id === props?.slideIndex || id === slideActive)
          return 'text-gray-darkest';
        return 'text-gray-500';
    }
  };

  const fontSizeVariant = (id: number) => {
    if (id === props?.slideIndex || id === slideActive) return 'text-h2';
    return 'text-h3';
  };

  return (
    <StyledContent css={props?.css} className={props?.className} {...options}>
      <Swiper
        modules={[Controller]}
        speed={speed}
        threshold={10}
        slidesPerView={'auto'}
        spaceBetween={0}
        centeredSlides
        centeredSlidesBounds={props?.centeredSlidesBounds}
        onSwiper={props?.onSwiper}
        controller={{
          control,
        }}
        initialSlide={initialSlide}
        loop={loop}
        onSlideChange={(e) => onItemSlideChange(e.realIndex)}
        data-test={`${props?.dataTest}_menus`}
      >
        {props?.items?.map((item, i) => (
          <SwiperSlide
            key={i}
            data-test={`${props?.dataTest}_menu_${i + 1}`}
            onClick={() => _onClick(i)}
          >
            <Box
              title={item?.preTitle}
              tracking={{
                dataTrack: props?.dataTrack,
                dataTrackSection: props?.dataTest,
                dataTrackText: item?.preTitle,
              }}
              data-test={`${props?.dataTest}_menu_${i + 1}`}
            >
              {!!item?.preTitle && (
                <LinupMenuText
                  size={fontSizeVariant(i)}
                  variant={textVariant(i, item?.theme)}
                  data-test={`${props?.dataTest}_menu_title_${i + 1}`}
                >
                  {item?.preTitle}
                </LinupMenuText>
              )}
            </Box>
          </SwiperSlide>
        ))}
      </Swiper>
    </StyledContent>
  );
};
LineupMenuSlider.displayName = 'LineupMenuSlider';
